import React, { FC } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

import type { ChartData } from './healthWalletSlice';
import { BRAND, BRAND_DARK, BRAND_DARKER, computeYDomain, formatYTick } from './healthWalletConstants';
import { ChartAiSummary, useChartSummaryStream } from './ChartAiSummary';
import { useAiSummaryEnabled } from './aiSettings';

// --- Custom chart dot — CLICK-select only. The old hover-sync state lived in EventTimeline, so every
// EVENT-row mouseenter re-rendered this entire Recharts SVG (the heaviest thing in the dialog) — the
// sluggish event list Leo reported. Hover selection removed (adds nothing over click; absent on mobile);
// the Recharts Tooltip still gives in-chart hover feedback for free (internal, no React state).
const ChartDot: FC<any> = (props) => {
  const { cx, cy, payload, selectedEvent, onClick } = props;
  const evIdx = payload?.eventIndex;
  const isSelected = selectedEvent === evIdx;
  return (
    <circle
      cx={cx} cy={cy}
      r={isSelected ? 6 : 3}
      fill={isSelected ? BRAND_DARKER : BRAND_DARK}
      stroke="white" strokeWidth={isSelected ? 2.5 : 1.5}
      style={{ cursor: 'pointer', outline: 'none' }}
      tabIndex={0}
      role="button"
      aria-label={`Data point: ${payload?.value ?? ''} on ${payload?.date ? new Date(payload.date).toLocaleDateString() : ''}`}
      onClick={() => onClick(evIdx)}
      onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(evIdx); } }}
    />
  );
};

// --- Quantitative Value Chart ---
export const ValueChart: FC<{
  chart: ChartData;
  resource: string;
  groupName: string;   // human label (chart title / aria)
  groupKey: string;    // analyte KEY — scopes the AI chart-context (sent as `name`, backend treats it as the key)
  selectedEvent: number | null;
  onClick: (i: number) => void;
}> = ({ chart, resource, groupName, groupKey, selectedEvent, onClick }) => {
  const chartData = chart.data.map(p => ({
    date: new Date(p.date).getTime(),
    value: p.value,
    eventIndex: p.eventIndex,
  }));

  const values = chart.data.map(p => p.value);
  const [yMin, yMax] = computeYDomain(values);

  // AI summary over SSE (no doctor questions on the compact Observations chart — Trends only). Scoped by the
  // analyte KEY (groupKey), not the display name — the backend chart-context front-door treats it as the key.
  const aiOn = useAiSummaryEnabled();
  const ai = useChartSummaryStream(resource, groupKey, aiOn);

  return (
    <Box sx={{ mb: 2.5 }} role="figure" aria-label={`Trend chart for ${groupName} in ${chart.unit}`}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, color: '#1c1c1e' }}>
        Trend ({chart.unit})
      </Typography>
      <Box sx={{ bgcolor: '#f7f7fa', borderRadius: 2, p: 1.5, pt: 2 }}>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 20, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#efeaff" />
            <XAxis
              dataKey="date" type="number" scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { year: '2-digit', month: 'short' })}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              type="number"
              domain={[yMin, yMax]}
              tickFormatter={formatYTick}
              tick={{ fontSize: 10 }}
              label={{ value: chart.unit, angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#6c4ce0' } }}
              width={45}
            />
            <RechartsTooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <Paper sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, boxShadow: 2 }}>
                    <Typography variant="caption" fontWeight={600} display="block">
                      {d.value} {chart.unit}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {new Date(d.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </Typography>
                  </Paper>
                );
              }}
            />
            <Line
              type="monotone" dataKey="value"
              stroke={BRAND_DARKER} strokeWidth={2}
              dot={<ChartDot selectedEvent={selectedEvent} onClick={onClick} />}
              activeDot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* AI Summary — real-SSE, step-aware; hides entirely on soft-fail (see ChartAiSummary). */}
      <ChartAiSummary variant="compact" status={ai.status} summary={ai.summary} loading={ai.loading} />
    </Box>
  );
};
